import { useEffect, useCallback, useRef } from 'react';
import { bridge } from '@/utils/postMessageBridge';
import type { InboundInit, OutboundMessage } from '@/types/bridge';

interface UsePostMessageOptions {
  onInit?: (payload: InboundInit['payload']) => void;
}

/**
 * 在 App 层调用，负责：
 * 1. 启动 bridge 监听
 * 2. 注册所有 inbound 消息的处理器
 * 3. 向父页面发送 ready 信号
 * 4. 组件卸载时自动清理
 */
export function usePostMessage(options: UsePostMessageOptions = {}): {
  send: (message: OutboundMessage) => void;
} {
  const onInitRef = useRef(options.onInit);
  onInitRef.current = options.onInit;

  useEffect(() => {
    bridge.listen();

    const disposers: Array<() => void> = [];

    disposers.push(
      bridge.on('init', (payload) => {
        onInitRef.current?.(payload);
      }),
    );

    disposers.push(
      bridge.on('set-visible', (_payload) => {
        // 预留：父页面通知面板可见性变化，可用于暂停/恢复动画等
      }),
    );

    bridge.send({ type: 'ready' });

    return () => {
      disposers.forEach((dispose) => dispose());
      bridge.destroy();
    };
  }, []);

  const send = useCallback((message: OutboundMessage) => {
    bridge.send(message);
  }, []);

  return { send };
}
